<template>
  <div>
    <section class="spacing-white">
      <h1>{{ title }}</h1>

      <InputBase
        v-model="currentSearch"
        :icon="IconSearch"
        :icon-left="true"
        type="search"
        placeholder="Search..."
        class="mb-8"
        @input="handleSearchInput"
      />
      <ul class="flex flex-col gap-6">
        <p
          v-if="
            (data && data.hits.length === 0 && currentSearch.length > 2) ||
            !data
          "
          class="italic text-grey-900"
        >
          No results found for
          <span class="font-medium">'{{ title }}''</span>.
        </p>
        <li
          v-for="item in data.hits
            .filter((hit: any) => hit.category === sortPriority)
            .slice(0, primaryArticles)"
          :key="item.objectID"
          class="mb-4"
        >
          <NuxtLink
            class="group flex items-start gap-6 text-grey-900 no-underline"
            :to="item.path"
          >
            <img
              class="hidden h-32 w-32 rounded shadow-md md:inline"
              :src="
                item.content &&
                item.content.image &&
                item.content.image.filename
                  ? `${item.content.image.filename}/m/256x256/smart/`
                  : `https://a-us.storyblok.com/f/1005128/2048x2048/19c7134f1b/aussie-broadband-profile-picture.png/m/256x256/smart/`
              "
              alt=""
            />
            <div>
              <p class="mb-2 font-semibold group-hover:underline">
                {{ item.title }}
              </p>
              <p class="mb-1 text-sm md:text-base">
                {{
                  item.description?.length
                    ? `${item.description.substring(0, 240)}...`
                    : "Read more..."
                }}
              </p>
              <div
                v-if="item.category"
                class="w-max rounded-full bg-grey-300 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-grey-700"
                v-html="item.category"
              />
            </div>
          </NuxtLink>
        </li>
      </ul>
      <div class="mt-4 flex gap-4">
        <button
          v-if="totalPrimaryArticles > primaryArticles"
          type="button"
          class="w-max rounded-full bg-grey-300 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-grey-700 hover:bg-grey-400"
          @click="addArticles('primary')"
        >
          Show more +
        </button>
        <button
          v-if="primaryArticles > 10"
          type="button"
          class="w-max rounded-full bg-grey-300 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-grey-700 hover:bg-grey-400"
          @click="subTrackArticles('primary')"
        >
          Show less -
        </button>
      </div>
    </section>
    <section class="spacing-white">
      <h3
        v-if="totalSecondaryArticles && totalSecondaryArticles > 0"
        class="mt-8 text-lg text-green-800"
      >
        Results from other pages
      </h3>
      <ul
        v-if="totalSecondaryArticles && totalSecondaryArticles > 0"
        class="flex flex-col gap-6"
      >
        <li
          v-for="item in data.hits
            .filter((hit: any) => hit.category !== sortPriority)
            .slice(0, secondaryArticles)"
          :key="item.objectID"
          class="mb-4"
        >
          <NuxtLink
            class="group flex items-start gap-6 text-grey-900 no-underline"
            :to="item.url"
          >
            <img
              class="hidden h-32 w-32 rounded shadow-md md:inline"
              :src="
                item.content &&
                item.content.image &&
                item.content.image.filename
                  ? `${item.content.image.filename}/m/256x256/smart/`
                  : `https://a-us.storyblok.com/f/1005128/2048x2048/19c7134f1b/aussie-broadband-profile-picture.png/m/256x256/smart/`
              "
              alt=""
            />
            <div class="">
              <p class="mb-2 font-semibold group-hover:underline">
                {{ item.title }}
              </p>
              <p class="mb-1 text-sm md:text-base">
                {{
                  item.description?.length
                    ? `${item.description.substring(0, 180)}...`
                    : "Read more..."
                }}
              </p>
              <div
                v-if="item.category"
                class="w-max rounded-full bg-grey-300 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-grey-700"
                v-html="item.category"
              />
            </div>
          </NuxtLink>
        </li>
      </ul>
      <div
        v-if="totalSecondaryArticles && totalSecondaryArticles > 0"
        class="mt-4 flex gap-4"
      >
        <button
          v-if="totalPrimaryArticles > secondaryArticles"
          type="button"
          class="w-max rounded-full bg-grey-300 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-grey-700 hover:bg-grey-400"
          @click="addArticles('secondary')"
        >
          Show more +
        </button>
        <button
          v-if="secondaryArticles > 10"
          type="button"
          class="w-max rounded-full bg-grey-300 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-grey-700 hover:bg-grey-400"
          @click="subTrackArticles('secondary')"
        >
          Show less -
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import IconSearch from "@/components/icon/Search.vue";
import type { Hit } from "@/types/algolia";
import type { SearchResponse } from "@algolia/client-search";

const props = defineProps<{
  title: string;
  tag: string;
  sortPriority: string;
}>();

const currentSearch = ref("");

const fetchTagData = async (query?: string) => {
  const response = await useAlgoliaSearch({
    query: query ?? "",
    requestOptions: {
      hitsPerPage: 1000,
      filters: `tags:'${props.tag}'`,
    },
  });

  return response;
};

const data = ref<SearchResponse<Hit> | null>();
data.value = await fetchTagData();

const totalPrimaryArticles = computed(
  () =>
    data.value?.hits.filter((hit: Hit) => hit.category === props.sortPriority)
      .length,
);

const totalSecondaryArticles = computed(
  () =>
    data.value?.hits.filter((hit: Hit) => hit.category !== props.sortPriority)
      .length,
);

const handleSearchInput = debounce(async () => {
  if (currentSearch.value.length > 2) {
    data.value = await fetchTagData(currentSearch.value);
  }
}, 500);

const primaryArticles = ref(10);
const secondaryArticles = ref(10);

const addArticles = (articleType: "primary" | "secondary") => {
  if (
    articleType === "primary" &&
    totalPrimaryArticles.value &&
    totalPrimaryArticles.value > primaryArticles.value
  ) {
    primaryArticles.value += 10;
  }
  if (
    articleType === "secondary" &&
    totalSecondaryArticles.value &&
    totalSecondaryArticles.value > secondaryArticles.value
  ) {
    secondaryArticles.value += 10;
  }
};

const subTrackArticles = (articleType: "primary" | "secondary") => {
  if (articleType === "primary" && primaryArticles.value > 10) {
    primaryArticles.value -= 10;
  }
  if (articleType === "secondary" && secondaryArticles.value > 10) {
    secondaryArticles.value -= 10;
  }
};
</script>
